import React, { useEffect, useRef, useState } from "react";

/**
 * Ambient backdrop that crossfades between two holographic videos as the user scrolls.
 * - SKULL hologram is dominant from Hero through Hidden Few
 * - SHIP hologram takes over starting at the BIG BORA EMPIRE section and stays to the end
 *
 * On iOS/iPadOS we fall back to a pure-CSS cinematic backdrop:
 * iPad Safari can only decode a limited number of concurrent videos, and
 * combining `position: fixed` + `mix-blend-mode` + animated opacity on video
 * elements makes momentum scroll jitter. Dropping the videos on iOS frees the
 * decoder for the PANDI reel and eliminates the glitching.
 */
const MAX_OPACITY = 0.55;

// Detect iOS + iPadOS (which pretends to be macOS Safari since iOS 13).
function isIOSOrIPad() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const iOSClassic = /iPad|iPhone|iPod/.test(ua);
  const iPadOS =
    ua.includes('Macintosh') &&
    typeof document !== 'undefined' &&
    'ontouchend' in document;
  return iOSClassic || iPadOS;
}

export default function HolographicSkullBg() {
  const skullRef = useRef(null);
  const shipRef = useRef(null);
  const akrAbsTopRef = useRef(null);
  const [useVideos, setUseVideos] = useState(true);

  // Decide once whether to render video backdrops.
  useEffect(() => {
    setUseVideos(!isIOSOrIPad());
  }, []);

  // Locate the AKR section after layout so we know where the crossfade starts.
  useEffect(() => {
    const findAkr = () => {
      const headings = Array.from(document.querySelectorAll("h2"));
      const akrHeading = headings.find((h) =>
        (h.textContent || "").includes("BIG BORA EMPIRE")
      );
      if (!akrHeading) return;
      const section = akrHeading.closest("section") || akrHeading;
      const r = section.getBoundingClientRect();
      akrAbsTopRef.current = r.top + window.scrollY;
      apply();
    };
    findAkr();
    const refind = () => findAkr();
    window.addEventListener("resize", refind);
    window.addEventListener("load", refind);
    const t1 = setTimeout(refind, 500);
    const t2 = setTimeout(refind, 1500);
    const t3 = setTimeout(refind, 3500);
    return () => {
      window.removeEventListener("resize", refind);
      window.removeEventListener("load", refind);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, []);

  // Compute phase from scroll and write it to whichever backdrop layer exists.
  const apply = () => {
    const skull = skullRef.current;
    const ship = shipRef.current;
    if (!skull || !ship) return;
    const akrAbs = akrAbsTopRef.current;
    const y = window.scrollY;
    const vh = window.innerHeight;
    let phase = 0;
    if (akrAbs != null) {
      const startScroll = akrAbs - vh;
      const endScroll = akrAbs - vh * 0.3;
      if (y <= startScroll) phase = 0;
      else if (y >= endScroll) phase = 1;
      else phase = (y - startScroll) / (endScroll - startScroll);
    }
    skull.style.opacity = String(MAX_OPACITY * (1 - phase));
    ship.style.opacity = String(MAX_OPACITY * phase);
  };

  useEffect(() => {
    const onScroll = () => apply();
    apply();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [useVideos]);

  // Kick the two background videos on desktop (unchanged).
  useEffect(() => {
    if (!useVideos) return;
    const start = () => {
      [skullRef.current, shipRef.current].forEach((v) => {
        if (v && v.tagName === 'VIDEO') {
          v.muted = true;
          const p = v.play();
          if (p && p.catch) p.catch(() => {});
        }
      });
    };
    start();
    const id = setInterval(start, 1500);
    document.addEventListener("click", start);
    document.addEventListener("touchstart", start);
    document.addEventListener("keydown", start);
    setTimeout(() => clearInterval(id), 6000);
    return () => {
      clearInterval(id);
      document.removeEventListener("click", start);
      document.removeEventListener("touchstart", start);
      document.removeEventListener("keydown", start);
    };
  }, [useVideos]);

  // Shared vignette + edge fades — used by both variants.
  const overlays = (
    <>
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 50% 50%, transparent 40%, rgba(8,7,5,0.45) 78%, rgba(8,7,5,0.8) 100%)",
        }}
      />
      <div
        className="absolute inset-x-0 top-0 h-[14vh]"
        style={{
          background:
            "linear-gradient(180deg, rgba(8,7,5,0.8) 0%, rgba(8,7,5,0.18) 70%, transparent 100%)",
        }}
      />
      <div
        className="absolute inset-x-0 bottom-0 h-[14vh]"
        style={{
          background:
            "linear-gradient(0deg, rgba(8,7,5,0.8) 0%, rgba(8,7,5,0.18) 70%, transparent 100%)",
        }}
      />
      <div
        className="absolute inset-0 mix-blend-overlay"
        style={{
          background:
            "linear-gradient(135deg, rgba(200,161,74,0.15) 0%, transparent 35%, rgba(106,53,34,0.1) 100%)",
        }}
      />
    </>
  );

  return (
    <div
      className="pointer-events-none fixed inset-0"
      style={{ zIndex: 0 }}
      aria-hidden
    >
      {useVideos ? (
        <>
          <video
            ref={skullRef}
            className="absolute inset-0 w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            style={{
              opacity: MAX_OPACITY,
              mixBlendMode: "screen",
              filter: "saturate(0.8) brightness(1.15) contrast(1.18)",
              transform: "scale(1.04)",
              animation: "skullbg-breathe 14s ease-in-out infinite",
              transition: "opacity 200ms linear",
            }}
          >
            <source src="/videos/skull.webm" type="video/webm" />
            <source src="/videos/skull.mp4" type="video/mp4" />
          </video>

          <video
            ref={shipRef}
            className="absolute inset-0 w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            style={{
              opacity: 0,
              mixBlendMode: "screen",
              filter: "saturate(0.8) brightness(1.15) contrast(1.15)",
              transform: "scale(1.05)",
              animation: "ship-pan 22s ease-in-out infinite alternate",
              transition: "opacity 200ms linear",
            }}
          >
            <source src="/videos/ship.webm" type="video/webm" />
            <source src="/videos/ship.mp4" type="video/mp4" />
          </video>
        </>
      ) : (
        <>
          {/* iOS / iPad: pure-CSS backdrops. No videos → no decoder pressure,
              no mix-blend-mode + fixed jitter during momentum scroll. */}
          <div
            ref={skullRef}
            className="absolute inset-0"
            style={{
              opacity: MAX_OPACITY,
              transition: "opacity 200ms linear",
              background:
                "radial-gradient(circle at 30% 40%, rgba(90,180,200,0.28) 0%, rgba(20,40,60,0.18) 30%, rgba(8,7,5,0) 60%), radial-gradient(circle at 70% 60%, rgba(200,161,74,0.14) 0%, rgba(8,7,5,0) 55%), linear-gradient(180deg, #0a0806 0%, #0e0b08 100%)",
              filter: "saturate(0.9) contrast(1.05)",
            }}
          />
          <div
            ref={shipRef}
            className="absolute inset-0"
            style={{
              opacity: 0,
              transition: "opacity 200ms linear",
              background:
                "radial-gradient(ellipse at 60% 30%, rgba(230,120,40,0.22) 0%, rgba(60,20,10,0.14) 35%, rgba(8,7,5,0) 65%), radial-gradient(circle at 25% 75%, rgba(180,50,30,0.14) 0%, rgba(8,7,5,0) 55%), linear-gradient(180deg, #0d0805 0%, #100a06 100%)",
              filter: "saturate(0.95) contrast(1.08)",
            }}
          />
        </>
      )}

      {overlays}
    </div>
  );
}
